import {
  parseStaticMapSize,
  parseStaticMapView,
  projectWebMercator,
  STATIC_MAP_OVERLAY_OFFSET_X,
  staticMapUrlWithoutMarkers,
  unprojectWebMercator,
} from "@/utils/solarLayoutOverlay";

/** Mesh-local metres: +x is raster east, +y is raster south. Origin is the raster's north-west corner. */
export type MeshGroundFrame = {
  originLongitude: number;
  originLatitude: number;
  eastLongitudePerM: number;
  eastLatitudePerM: number;
  southLongitudePerM: number;
  southLatitudePerM: number;
};

export type MeshWorldCenter = { x: number; z: number };

export type SatelliteGroundRect = {
  width: number;
  depth: number;
  x: number;
  z: number;
};

export function meshGroundFrameFromStats(raw: unknown): MeshGroundFrame | null {
  if (!raw || typeof raw !== "object") return null;
  const frame = raw as Record<string, unknown>;
  const originLongitude = Number(frame.origin_longitude);
  const originLatitude = Number(frame.origin_latitude);
  const eastLongitudePerM = Number(frame.east_longitude_per_m);
  const eastLatitudePerM = Number(frame.east_latitude_per_m);
  const southLongitudePerM = Number(frame.south_longitude_per_m);
  const southLatitudePerM = Number(frame.south_latitude_per_m);
  const values = [
    originLongitude,
    originLatitude,
    eastLongitudePerM,
    eastLatitudePerM,
    southLongitudePerM,
    southLatitudePerM,
  ];
  if (values.some((value) => !Number.isFinite(value))) return null;
  return {
    originLongitude,
    originLatitude,
    eastLongitudePerM,
    eastLatitudePerM,
    southLongitudePerM,
    southLatitudePerM,
  };
}

/** Invert the local east/south basis so a WGS84 point lands in mesh metres. */
export function latLngToMeshLocal(
  latitude: number,
  longitude: number,
  frame: MeshGroundFrame,
): { x: number; y: number } | null {
  const dLng = longitude - frame.originLongitude;
  const dLat = latitude - frame.originLatitude;
  const det = frame.eastLongitudePerM * frame.southLatitudePerM
    - frame.southLongitudePerM * frame.eastLatitudePerM;
  if (!Number.isFinite(det) || Math.abs(det) < 1e-18) return null;
  return {
    x: (dLng * frame.southLatitudePerM - dLat * frame.southLongitudePerM) / det,
    y: (frame.eastLongitudePerM * dLat - frame.eastLatitudePerM * dLng) / det,
  };
}

/**
 * Same transform as the viewer: mirror east-west, then lay elevation up, then centre the building.
 * +world X is west, +world Z is north.
 */
export function meshLocalToWorld(
  local: { x: number; y: number },
  center: MeshWorldCenter,
): { x: number; z: number } {
  return {
    x: -local.x - center.x,
    z: -local.y - center.z,
  };
}

type BoundaryGeometry = {
  type?: string;
  coordinates?: unknown;
  geometry?: BoundaryGeometry | null;
  features?: Array<{ geometry?: BoundaryGeometry | null }>;
};

/** Closed title rings in viewer space, using the same frame as the satellite ground. */
export function boundaryWorldRings(
  geometry: BoundaryGeometry | null | undefined,
  frame: MeshGroundFrame,
  center: MeshWorldCenter,
): Array<Array<{ x: number; z: number }>> {
  return boundaryRings(geometry).flatMap((ring) => {
    const world = ring.flatMap(([longitude, latitude]) => {
      const local = latLngToMeshLocal(latitude, longitude, frame);
      return local ? [meshLocalToWorld(local, center)] : [];
    });
    if (world.length < 3) return [];
    const first = world[0];
    const last = world[world.length - 1];
    if (Math.hypot(first.x - last.x, first.z - last.z) < 0.05) world.pop();
    return world.length >= 3 ? [world] : [];
  });
}

function boundaryRings(geometry: BoundaryGeometry | null | undefined): number[][][] {
  if (!geometry) return [];
  if (geometry.type === "Feature") return boundaryRings(geometry.geometry);
  if (geometry.type === "FeatureCollection") {
    return (geometry.features ?? []).flatMap((feature) => boundaryRings(feature.geometry));
  }
  if (!Array.isArray(geometry.coordinates)) return [];
  if (geometry.type === "Polygon") return geometry.coordinates as number[][][];
  if (geometry.type === "MultiPolygon") {
    return (geometry.coordinates as number[][][][]).flat();
  }
  return [];
}

function staticMapPixelToLatLng(
  pixelX: number,
  pixelY: number,
  center: { latitude: number; longitude: number },
  zoom: number,
  width: number,
  height: number,
) {
  const origin = projectWebMercator(center.latitude, center.longitude);
  const scale = 2 ** zoom;
  return unprojectWebMercator(
    origin.x + (pixelX - width / 2) / scale,
    origin.y + (pixelY - height / 2) / scale,
  );
}

/**
 * Ground rectangle, in the centred viewer, covering the Static Maps photo.
 * Width runs west–east and depth runs north–south.
 */
export function satelliteGroundRect(
  satelliteImageUrl: string,
  frame: MeshGroundFrame,
  center: MeshWorldCenter,
): SatelliteGroundRect | null {
  const view = parseStaticMapView(staticMapUrlWithoutMarkers(satelliteImageUrl));
  if (!view) return null;
  const size = parseStaticMapSize(view.size);
  const corners = [
    [0, 0],
    [size.width, 0],
    [size.width, size.height],
    [0, size.height],
  ].map(([pixelX, pixelY]) => {
    const latLng = staticMapPixelToLatLng(
      pixelX,
      pixelY,
      view.center,
      view.zoom,
      size.width,
      size.height,
    );
    const local = latLngToMeshLocal(latLng.latitude, latLng.longitude, frame);
    return local ? meshLocalToWorld(local, center) : null;
  });
  if (corners.some((corner) => corner == null)) return null;
  const [northWest, northEast, southEast, southWest] = corners as { x: number; z: number }[];
  const width = (Math.abs(northEast.x - northWest.x) + Math.abs(southEast.x - southWest.x)) / 2;
  const depth = (Math.abs(northWest.z - southWest.z) + Math.abs(northEast.z - southEast.z)) / 2;
  if (width < 1 || depth < 1) return null;
  return {
    width,
    depth,
    x: (northWest.x + northEast.x + southEast.x + southWest.x) / 4,
    z: (northWest.z + northEast.z + southEast.z + southWest.z) / 4,
  };
}

/**
 * World-X shift that matches the satellite view's overlay correction.
 * Positive image X is east; the returned value is in the viewer's mirrored frame.
 */
export function staticMapOverlayShiftX(
  satelliteImageUrl: string | null | undefined,
  frame: MeshGroundFrame,
  center: MeshWorldCenter,
): number {
  const view = parseStaticMapView(staticMapUrlWithoutMarkers(satelliteImageUrl ?? ""));
  if (!view) return 0;
  const size = parseStaticMapSize(view.size);
  const middleY = size.height / 2;
  const west = staticMapPixelToLatLng(0, middleY, view.center, view.zoom, size.width, size.height);
  const east = staticMapPixelToLatLng(size.width, middleY, view.center, view.zoom, size.width, size.height);
  const westLocal = latLngToMeshLocal(west.latitude, west.longitude, frame);
  const eastLocal = latLngToMeshLocal(east.latitude, east.longitude, frame);
  if (!westLocal || !eastLocal) return 0;
  const westWorld = meshLocalToWorld(westLocal, center);
  const eastWorld = meshLocalToWorld(eastLocal, center);
  return ((eastWorld.x - westWorld.x) / size.width) * STATIC_MAP_OVERLAY_OFFSET_X;
}
